// Servicio para manejar el cambio de agente asignado en conversaciones (batch y rollback optimista)
import { updateOrInsertConversationOptimized } from '../../actions/inbox/conversationOptimizedActions';
import ChatActionsModelMC from '../../presentation/models/inbox/ChatActionsModelMC';
import { assertMsInboxUrlConfigured } from './msInboxClient';
import { assignAgentMsInbox } from './msInboxAssignAgentClient';
import { isInboxMsChat } from './inboxMsChatPlatform';

function validateBodies(bodies, dispatch) {
  if (!Array.isArray(bodies) || bodies.length === 0) {
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: 'No conversations provided to change customer agent'
    });
    return false;
  }
  return true;
}

function partitionByPlatform(bodies) {
  const msBodies = bodies.filter((b) => isInboxMsChat(b.platform));
  const mercatelyBodies = bodies.filter((b) => !isInboxMsChat(b.platform));
  return { msBodies, mercatelyBodies };
}

function buildAssignPayload(bodies) {
  const _agent = bodies[0]?.newAgent?.id === 'assign_agent' ? null : bodies[0]?.newAgent?.id;
  const retailer_user_id = _agent || null;
  const assignments = bodies.map(({ id, platform }) => ({ id, platform }));
  return { retailer_user_id, assignments };
}

function unwrapBatchData(json) {
  if (!json || typeof json !== 'object') return {};
  if (json.summary && Array.isArray(json.successful_assignments)) {
    return json;
  }
  if (json.data && typeof json.data === 'object' && json.data.summary) {
    return json.data;
  }
  return json;
}

function mergeBatchAssignResponses(monolithJson, tiktokJson) {
  const a = unwrapBatchData(monolithJson);
  const b = unwrapBatchData(tiktokJson);
  const summary = {
    total: (a.summary?.total || 0) + (b.summary?.total || 0),
    successful: (a.summary?.successful || 0) + (b.summary?.successful || 0),
    failed: (a.summary?.failed || 0) + (b.summary?.failed || 0)
  };
  const successful_assignments = [
    ...(a.successful_assignments || []),
    ...(b.successful_assignments || [])
  ];
  const failed_assignments = [
    ...(a.failed_assignments || []),
    ...(b.failed_assignments || [])
  ];
  const parts = [a.message, b.message].filter(Boolean);
  const message = parts.join(' | ');
  return {
    summary,
    successful_assignments,
    failed_assignments,
    message
  };
}

async function updateLocalStates(dispatch, bodies) {
  return Promise.all(bodies.map(async (body) => {
    const { id, platform, newAgent } = body || {};
    if (!id || !platform || !newAgent) {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: `Missing data to change customer agent for id: ${id}`
      });
      return { id, success: false, error: 'Missing data to change customer agent' };
    }
    const changes = { assigned_agent: { id: newAgent.id } };
    const updateResult = await dispatch(updateOrInsertConversationOptimized({
      id,
      changes
    }));
    if (!updateResult.success) {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: updateResult.error || `Error updating conversation agent for id: ${id}`
      });
      return { id, success: false, error: updateResult.error };
    }
    return {
      id,
      platform,
      newAgent,
      success: true
    };
  }));
}

async function rollbackLocalStates(dispatch, bodies, options, failedIds = null) {
  return Promise.all(bodies.map(async (body, idx) => {
    const { id } = body || {};
    if (failedIds !== null && failedIds.length > 0) {
      const keep = failedIds.some((fid) => String(fid) === String(id));
      if (!keep) return;
    }
    const entry = options?.[idx];
    if (!entry || !Object.prototype.hasOwnProperty.call(entry, 'prevAgent')) return;
    await dispatch(updateOrInsertConversationOptimized({
      id,
      changes: { assigned_agent: entry.prevAgent ?? null }
    }));
  }));
}

async function sendBatchToServer(bodies) {
  if (!bodies.length) return;

  const { msBodies, mercatelyBodies } = partitionByPlatform(bodies);
  const hasMercately = mercatelyBodies.length > 0;
  const hasMs = msBodies.length > 0;

  if (hasMs) {
    assertMsInboxUrlConfigured();
  }

  if (hasMercately && !hasMs) {
    await ChatActionsModelMC.assignAgent(buildAssignPayload(mercatelyBodies));
    return;
  }

  if (!hasMercately && hasMs) {
    await assignAgentMsInbox(buildAssignPayload(msBodies));
    return;
  }

  const settled = await Promise.allSettled([
    ChatActionsModelMC.assignAgent(buildAssignPayload(mercatelyBodies)),
    assignAgentMsInbox(buildAssignPayload(msBodies))
  ]);

  const mercatelySettled = settled[0];
  const msSettled = settled[1];
  const mercatelyFailed = mercatelySettled.status === 'rejected';
  const msFailed = msSettled.status === 'rejected';

  if (mercatelyFailed || msFailed) {
    const msgParts = [];
    if (mercatelyFailed) {
      const r = mercatelySettled.reason;
      msgParts.push(r?.message ? r.message : String(r));
    }
    if (msFailed) {
      const r = msSettled.reason;
      msgParts.push(r?.message ? r.message : String(r));
    }
    const err = new Error(
      msgParts.filter(Boolean).join(' | ') || 'Error changing customer agent (batch)'
    );
    err.failedIds = [
      ...(mercatelyFailed ? mercatelyBodies.map((b) => b.id) : []),
      ...(msFailed ? msBodies.map((b) => b.id) : [])
    ];
    throw err;
  }

  mergeBatchAssignResponses(mercatelySettled.value, msSettled.value);
}

function expandWithSameCustomer(bodies, options, getState) {
  const state = getState();
  const conversations = state.conversationsReducer?.data;
  if (!Array.isArray(conversations) || conversations.length === 0) {
    return { allBodies: bodies, allOptions: options };
  }

  const originalIds = new Set(bodies.map((b) => String(b.id)));
  const allBodies = [...bodies];
  const allOptions = [...options];

  for (let i = 0; i < bodies.length; i++) {
    const body = bodies[i];
    const conv = conversations.find((c) => String(c.id) === String(body.id));
    const customerId = conv?.attributes?.customer_id;
    if (!customerId) continue;

    const sameCustomerConvs = conversations.filter(
      (c) => c.attributes?.customer_id === customerId && !originalIds.has(String(c.id))
    );

    for (const c of sameCustomerConvs) {
      originalIds.add(String(c.id));
      allBodies.push({
        id: c.id,
        platform: c.attributes?.platform || body.platform,
        newAgent: body.newAgent
      });
      allOptions.push({
        id: c.id,
        prevAgent: c.attributes?.assigned_agent ?? null
      });
    }
  }

  return { allBodies, allOptions };
}

/**
 * Cambia el agente asignado a una o varias conversaciones y maneja rollback en caso de error, concurrentemente.
 * También actualiza visualmente las demás conversaciones del mismo customer (mismo customer_id) en la colección.
 * @param {Function} dispatch - Función dispatch de Redux.
 * @param {Function} getState - Función getState de Redux (thunk middleware).
 * @param {Array<Object>} bodies - Array de objetos con datos para el cambio de agente.
 * @param {Array<Object>} options - Array de opciones adicionales (ejemplo: prevAgent).
 * @returns {Promise<Object>} Resultado de la operación con éxito o error por cada chat.
 */
export default async function handleChangeCustomerAgent(dispatch, getState, bodies, options = []) {
  if (!validateBodies(bodies, dispatch)) {
    return { success: false, error: 'No conversations provided to change customer agent' };
  }

  // Expandir con conversaciones del mismo customer (visual sync)
  const { allBodies, allOptions } = typeof getState === 'function'
    ? expandWithSameCustomer(bodies, options, getState)
    : { allBodies: bodies, allOptions: options };

  // 1. Actualizar estado local concurrentemente (para todos: originales + mismo customer)
  const localResults = await updateLocalStates(dispatch, allBodies);
  if (localResults.some((r) => !r.success)) {
    return { success: false, results: localResults };
  }

  // 2. Enviar al servidor solo los originales (el backend maneja customer-level via MS)
  try {
    await sendBatchToServer(bodies);
    return { success: true, results: localResults };
  } catch (error) {
    const originalFailedIds = Array.isArray(error.failedIds) && error.failedIds.length > 0
      ? error.failedIds
      : null;

    // En error parcial, incluir también las expansiones en el rollback
    const rollbackIds = originalFailedIds !== null
      ? [
          ...originalFailedIds.map((id) => String(id)),
          ...allBodies
            .filter((b) => !bodies.some((ob) => String(ob.id) === String(b.id)))
            .map((b) => String(b.id))
        ]
      : null;

    await rollbackLocalStates(dispatch, allBodies, allOptions, rollbackIds);
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: error.message || 'Error changing customer agent (batch)'
    });
    return { success: false, error: error.message, results: localResults };
  }
}
